'use client';

import { c as _c } from "react/compiler-runtime";
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import type { MPAInterceptedResponse, MPAPolicy } from '@repo/api-gw-sdk';
import { MPAOperationScope } from '@repo/api-gw-sdk';
import { Fragment, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { Icon } from '@/components/shared/icon';
import { useDAL } from '@/contexts/useDAL';
import { MpaOperations } from '@/data/inventory/data';
import { BackupPolicyEntity, ResourceEntity, getBackupPolicyId, getResourceId } from './mpaRequestEntity';
type Pending = 'approve' | 'cancel' | null;
const formatHours = (hours: number) => `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
const formatApprovers = (n: number) => `${n} ${n === 1 ? 'approver' : 'approvers'}`;
export function MpaInterceptDialogContent({
  payload,
  onApprove,
  onCancel
}: {
  payload: MPAInterceptedResponse;
  onApprove: (comment: string) => Promise<void>;
  onCancel: () => Promise<void>;
}) {
  const dal = useDAL();
  const {
    data: operationsBody
  } = dal.mpa.operations.useList();
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const policies = payload.mpaPolicies?.policies ?? [];
  const operation = policies[0]?.operation;
  const operationName = operation ? MpaOperations[operation]?.name ?? '' : '';
  const scope = operation ? operationsBody?.operations.find(o => o.operation === operation)?.scope : undefined;
  const isBackupPolicyScope = scope === MPAOperationScope.MPA_OPERATION_SCOPE_BACKUP_POLICY;
  const isCloudResourceScope = scope === MPAOperationScope.MPA_OPERATION_SCOPE_CLOUD_RESOURCE;
  const run = (kind: Exclude<Pending, null>, action: () => Promise<void>) => async () => {
    setError(null);
    setPending(kind);
    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
      setPending(null);
    }
  };
  const handleApprove = run('approve', () => onApprove(comment));
  const handleCancel = run('cancel', onCancel);
  const disabled = pending !== null;
  return <Stack data-testid='mpa-intercept-dialog' className='h-full' sx={{
    overflow: 'hidden'
  }}>
      <Stack alignItems='center' gap='24px' sx={{
      flexShrink: 0
    }}>
        <Stack alignItems='center' gap='24px' sx={{
        width: '480px',
        textAlign: 'center',
        pt: '8px'
      }}>
          <Icon iconClass='material-symbols-approval-outline' />
          <Typography variant='h2'>Approval required</Typography>
          <Typography variant='body1' color='text.secondary'>
            This action requires approval. Once your request is approved,
            you&apos;ll have a limited time to apply it yourself.
          </Typography>
        </Stack>

        <Stack gap='8px' sx={{
        width: '582px'
      }}>
          <Typography variant='subtitle1'>
            Note for approvers (optional)
          </Typography>
          <TextField fullWidth multiline minRows={2} size='small' placeholder='Tell approvers why this action is needed.' value={comment} onChange={e_0 => setComment(e_0.target.value)} disabled={disabled} />
        </Stack>
      </Stack>

      <Stack alignItems='center' sx={{
      flex: 1,
      minHeight: 0,
      overflow: 'auto',
      pt: '24px'
    }}>
        <Stack sx={{
        width: '582px',
        pb: '24px'
      }}>
          <Stack gap='12px' sx={{
          backgroundColor: 'var(--mui-palette-background-secondary)',
          borderRadius: '8px',
          px: '40px',
          py: '24px'
        }}>
            <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap='24px'>
              <Typography variant='body1' sx={{
              width: '120px'
            }}>
                Action name
              </Typography>
              <Typography variant='h4'>{operationName}</Typography>
            </Stack>
            {isBackupPolicyScope && <Stack direction='row' justifyContent='space-between' alignItems='center' gap='24px'>
                <Typography variant='body1' sx={{
              width: '120px'
            }}>
                  Entity
                </Typography>
                <BackupPolicyEntity policyId={getBackupPolicyId(payload.mpaRequest)} />
              </Stack>}
            {isCloudResourceScope && <Stack direction='row' justifyContent='space-between' alignItems='center' gap='24px'>
                <Typography variant='body1' sx={{
              width: '120px'
            }}>
                  Resource
                </Typography>
                <ResourceEntity resourceId={getResourceId(payload.mpaRequest)} />
              </Stack>}
          </Stack>

          {policies.map(policy => <Fragment key={policy.id}>
              <Connector />
              <PolicyApprovalCard policy={policy} operationName={operationName} />
            </Fragment>)}

          {error && <Typography color='error' variant='body2' sx={{
          pt: '16px'
        }}>
              {error}
            </Typography>}
        </Stack>
      </Stack>

      <Stack direction='row' gap='12px' justifyContent='flex-end' alignItems='center' sx={{
      flexShrink: 0,
      backgroundColor: 'var(--mui-palette-background-paper)',
      boxShadow: 'var(--mui-customShadows-panelMd)',
      px: '40px',
      py: '16px'
    }}>
        <Button variant='text' onClick={() => void handleCancel()} disabled={disabled} data-testid='mpa-intercept-cancel'>
          Cancel Action
        </Button>
        <Button variant='contained' onClick={() => void handleApprove()} disabled={disabled} data-testid='mpa-intercept-approve'>
          Request Approval
        </Button>
      </Stack>
    </Stack>;
}
const Connector = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Box sx={{
      width: "1px",
      height: "24px",
      ml: "40px",
      backgroundColor: "var(--mui-palette-divider)"
    }} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const PolicyApprovalCard = t0 => {
  const $ = _c(31);
  const {
    policy,
    operationName
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      backgroundColor: "var(--mui-palette-background-secondary)",
      borderRadius: "8px",
      pt: "40px",
      pb: "12px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      px: "40px"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== operationName) {
    t3 = <Typography variant="body1">{"For "}<Typography component="span" variant="h4">{operationName},{" "}</Typography>{"those approval steps are required:"}</Typography>;
    $[2] = operationName;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      pb: "24px"
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== policy.requiredApprovals) {
    t5 = formatApprovers(policy.requiredApprovals);
    $[5] = policy.requiredApprovals;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t5) {
    t6 = <ApprovalStepRow label="From" value={t5} />;
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== policy.approvalWindowHours) {
    t7 = formatHours(policy.approvalWindowHours);
    $[9] = policy.approvalWindowHours;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== t7) {
    t8 = <ApprovalStepRow label="Within" value={t7} />;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <ApprovalStep icon="material-symbols-approval-outline" title="Get approval">{t6}{t8}</ApprovalStep>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== policy.executionWindowHours) {
    t10 = formatHours(policy.executionWindowHours);
    $[16] = policy.executionWindowHours;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10) {
    t11 = <ApprovalStep icon="material-symbols-ads-click" title="Apply the action" isLast={true}><ApprovalStepRow label="Within" value={t10} suffix="after approval" /></ApprovalStep>;
    $[18] = t10;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t11 || $[21] !== t9) {
    t12 = <Stack gap="24px" sx={t4}>{t9}{t11}</Stack>;
    $[20] = t11;
    $[21] = t9;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t12 || $[24] !== t3) {
    t13 = <Stack gap="24px" sx={t2}>{t3}{t12}</Stack>;
    $[23] = t12;
    $[24] = t3;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== policy.description) {
    t14 = policy.description && <><Box sx={{
        height: "1px",
        backgroundColor: "var(--mui-palette-divider)",
        width: "100%"
      }} /><Accordion sx={{
        px: "40px"
      }}><AccordionSummary><Typography variant="h4">Description</Typography></AccordionSummary><AccordionDetails sx={{
          p: 0,
          pl: "36px",
          pb: "8px"
        }}><Typography variant="body1" color="text.secondary" sx={{
            whiteSpace: "pre-wrap"
          }}>{policy.description}</Typography></AccordionDetails></Accordion></>;
    $[26] = policy.description;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== t13 || $[29] !== t14) {
    t15 = <Stack gap="24px" sx={t1}>{t13}{t14}</Stack>;
    $[28] = t13;
    $[29] = t14;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  return t15;
};
const ApprovalStep = t0 => {
  const $ = _c(19);
  const {
    icon,
    title,
    isLast,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      alignSelf: "stretch"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== icon) {
    t2 = <Icon iconClass={icon} color="var(--mui-palette-text-disabled)" />;
    $[1] = icon;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== isLast) {
    t3 = !isLast && <Box sx={{
      flex: 1,
      minHeight: "24px",
      width: 0,
      borderLeft: "1px dashed var(--mui-palette-divider)"
    }} />;
    $[3] = isLast;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <Stack alignItems="center" gap="16px" sx={t1}>{t2}{t3}</Stack>;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      flex: 1,
      minWidth: 0
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== title) {
    t6 = <Typography variant="h3" color="text.disabled">{title}</Typography>;
    $[9] = title;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== children) {
    t7 = <Stack gap="12px">{children}</Stack>;
    $[11] = children;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <Stack sx={t5} gap="16px" paddingTop="4px">{t6}{t7}</Stack>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t8) {
    t9 = <Stack direction="row" gap="24px" alignItems="stretch">{t4}{t8}</Stack>;
    $[16] = t4;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  return t9;
};
const ApprovalStepRow = t0 => {
  const $ = _c(11);
  const {
    label,
    value,
    suffix
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      width: "40px"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== label) {
    t2 = <Typography variant="subtitle1" sx={t1}>{label}</Typography>;
    $[1] = label;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== value) {
    t3 = <Typography variant="h4">{value}</Typography>;
    $[3] = value;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== suffix) {
    t4 = suffix && <Typography variant="subtitle1">{suffix}</Typography>;
    $[5] = suffix;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    t5 = <Stack direction="row" alignItems="center" gap="12px">{t2}{t3}{t4}</Stack>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};